import React, { useState, useEffect, useRef } from 'react';

interface CountdownTarget {
  timestamp: number; // Unix timestamp in milliseconds
  label: string;
  description?: string;
}

interface DynamicFaviconProps {
  countdownTarget?: CountdownTarget | null;
  faviconUrl?: string; // Add prop for favicon URL from API
  isDarkMode?: boolean; // Add prop to track current theme mode
}

// Utility function to interpolate between two colors
function interpolateColor(color1: string, color2: string, factor: number): string {
  // Convert hex to RGB
  const hex2rgb = (hex: string): [number, number, number] => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return [r, g, b];
  };

  // Get RGB values
  const [r1, g1, b1] = hex2rgb(color1);
  const [r2, g2, b2] = hex2rgb(color2);

  // Calculate interpolated values
  const r = Math.round(r1 + factor * (r2 - r1));
  const g = Math.round(g1 + factor * (g2 - g1));
  const b = Math.round(b1 + factor * (b2 - b1));

  // Convert back to hex
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

export const DynamicFavicon: React.FC<DynamicFaviconProps> = ({ 
  countdownTarget, 
  faviconUrl,
  isDarkMode = false
}) => {
  // Colors for countdown mode
  const countdownColors = {
    pending: '#EF4444', // red
    complete: '#10B981', // green
  };

  const [isAnimating, setIsAnimating] = useState(false);
  const [countdownComplete, setCountdownComplete] = useState(false);
  const animationRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const lastFaviconUrlRef = useRef<string | undefined>(undefined);

  // Set the favicon to the URL provided by the API
  const setFaviconUrl = (url: string) => {
    // Find the existing favicon
    let link: HTMLLinkElement | null = document.querySelector('link[rel="icon"]');
    
    // If no favicon exists, create one
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      link.type = 'image/png';
      document.head.appendChild(link);
    }
    
    // Update the favicon href
    link.href = url;
    
    // Update the ref to track changes
    lastFaviconUrlRef.current = url;
  };

  // Create a favicon with the current color using canvas
  const createFavicon = (color: string) => {
    // Only create custom favicon if no URL is provided
    if (faviconUrl) {
      setFaviconUrl(faviconUrl);
      return;
    }
    
    // Create a canvas element
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
    const context = canvas.getContext('2d');

    if (context) {
      // Draw a rounded square for the favicon (compatible with all browsers)
      context.beginPath();
      const radius = 8;

      // Draw rounded rectangle manually for maximum compatibility
      context.moveTo(radius, 0);
      context.lineTo(32 - radius, 0);
      context.quadraticCurveTo(32, 0, 32, radius);
      context.lineTo(32, 32 - radius);
      context.quadraticCurveTo(32, 32, 32 - radius, 32);
      context.lineTo(radius, 32);
      context.quadraticCurveTo(0, 32, 0, 32 - radius);
      context.lineTo(0, radius);
      context.quadraticCurveTo(0, 0, radius, 0);

      context.fillStyle = color;
      context.fill();

      // Add a simple "OS" text in the center
      context.fillStyle = '#FFFFFF';
      context.font = 'bold 16px Arial';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText('OS', 16, 16);

      // Convert canvas to data URL and update the favicon
      const dataUrl = canvas.toDataURL('image/png');
      
      // Update the favicon
      setFaviconUrl(dataUrl);
    }
  };

  // Check if countdown has been completed
  const checkCountdownStatus = () => {
    if (!countdownTarget) return false;
    
    const now = new Date().getTime();
    const isComplete = now >= countdownTarget.timestamp;
    
    if (isComplete !== countdownComplete) {
      setCountdownComplete(isComplete);
    }
    
    return isComplete;
  };

  // Animation function for smooth color transition
  const animateColorTransition = (timestamp: number) => {
    if (!startTimeRef.current) {
      startTimeRef.current = timestamp;
    }

    const elapsed = timestamp - startTimeRef.current;
    const animationDuration = 1000; // 1 second animation
    const progress = Math.min(elapsed / animationDuration, 1);

    // If we have a countdown, transition based on its status
    const isComplete = checkCountdownStatus();
    const fromColor = isComplete ? countdownColors.pending : countdownColors.complete;
    const toColor = isComplete ? countdownColors.complete : countdownColors.pending;
    
    const interpolatedColor = interpolateColor(fromColor, toColor, progress);
    createFavicon(interpolatedColor);

    if (progress < 1) {
      // Continue animation
      animationRef.current = requestAnimationFrame(animateColorTransition);
    } else {
      // Animation complete
      setIsAnimating(false);
      startTimeRef.current = 0;
    }
  };

  // Start color transition animation
  const startAnimation = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      animationRef.current = requestAnimationFrame(animateColorTransition);
    }
  };

  // Special effect for countdown mode: update the favicon when countdown status changes
  useEffect(() => {
    if (countdownTarget && !isAnimating) {
      startAnimation();
    }
  }, [countdownComplete, countdownTarget]);

  // Effect to handle favicon URL updates
  useEffect(() => {
    // If the favicon URL has changed or is first set, update it
    if (faviconUrl && faviconUrl !== lastFaviconUrlRef.current) {
      setFaviconUrl(faviconUrl);
    } else if (!faviconUrl && countdownTarget) {
      // If no favicon URL but we have a countdown, use countdown colors
      const initialColor = checkCountdownStatus() ? 
        countdownColors.complete : countdownColors.pending;
      createFavicon(initialColor);
      
      // Check the status periodically
      const statusChecker = setInterval(() => {
        const newStatus = checkCountdownStatus();
        if (newStatus !== countdownComplete && !isAnimating) {
          startAnimation();
        }
      }, 1000);
      
      return () => clearInterval(statusChecker);
    } else if (!faviconUrl) {
      // If no favicon URL and no countdown, use default color
      createFavicon('#3B82F6'); // Default blue color
    }
  }, [faviconUrl, countdownTarget, isDarkMode]);
  
  // This component doesn't render anything visible
  return null;
};

export default DynamicFavicon; 